import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Star, ShieldCheck, Package, MapPin, ArrowUpLeft, X, Lock, Flame, Truck, Clock, RefreshCw, Ruler } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { REVIEWS } from "../components/Reviews";
import { Reveal, EASE } from "../components/motion";
import { fetchProduct, fetchProducts, gbp, submitShopifyCheckout, REVIEW_RATING } from "../lib/shop";
import { trackEvent } from "../lib/analytics";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

function SizeGuide({ guide, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[85] flex items-center justify-center p-5" data-testid="size-guide-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="relative bg-coal border border-white/10 w-full max-w-2xl p-8 max-h-[85vh] overflow-y-auto"
        data-lenis-prevent
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-ash hover:text-bone transition-colors" aria-label="Close size guide" data-testid="size-guide-close-button">
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <p className="font-mono text-[10px] tracking-[0.25em] text-ash uppercase mb-3">Fit &amp; measurements</p>
        <h3 className="font-display font-extrabold uppercase tracking-tighter text-2xl mb-6">SIZE GUIDE</h3>
        {guide ? (
          <>
            <table className="w-full text-center font-mono text-xs" data-testid="size-guide-table">
              <thead>
                <tr className="text-ash border-b border-white/15">
                  {guide.columns.map((c) => (
                    <th key={c} className="py-3 font-normal tracking-[0.15em]">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {guide.rows.map((row, i) => (
                  <tr key={i} className="border-b border-white/5">
                    {row.map((cell, j) => (
                      <td key={j} className={`py-3 ${j === 0 ? "text-bone font-bold" : "text-bone/80"}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-ash text-xs leading-relaxed mt-5">
              Measurements are in centimetres (cm). Allow 1–2cm variation due to manual measurement. If you're between sizes, we recommend sizing up for a more relaxed fit.
            </p>
          </>
        ) : (
          <p className="text-ash text-sm">Adjustable snapback — one size fits most.</p>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function ProductPage() {
  const { handle } = useParams();
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [colour, setColour] = useState(0);
  const [size, setSize] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [showGuide, setShowGuide] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { add, openCart } = useCart();
  const actionsRef = useRef(null);
  const sizeRowRef = useRef(null);
  const pulseTimer = useRef(null);
  const [showSticky, setShowSticky] = useState(false);
  const [sizePulse, setSizePulse] = useState(false);

  // sticky bar appears only once the main buy box has scrolled out of view,
  // so it never covers the size selector or the add-to-cart actions
  useEffect(() => {
    if (!product || !actionsRef.current) return;
    const obs = new IntersectionObserver(([e]) => setShowSticky(!e.isIntersecting), { threshold: 0 });
    obs.observe(actionsRef.current);
    return () => obs.disconnect();
  }, [product, handle]);

  useEffect(() => () => clearTimeout(pulseTimer.current), []);

  const focusSizes = () => {
    sizeRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setSizePulse(true);
    clearTimeout(pulseTimer.current);
    pulseTimer.current = setTimeout(() => setSizePulse(false), 1800);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setNotFound(false);
    fetchProduct(handle).then((p) => {
      setProduct(p);
      setColour(0);
      setSize(p.sizes.length === 1 ? p.sizes[0] : null);
      setImgIdx(0);
      trackEvent("view_item", { value: p.price / 100, items: [{ item_id: p.handle, item_name: p.title, price: p.price / 100, item_category: p.category }] });
    }).catch(() => setNotFound(true));
    fetchProducts().then(setAll).catch(() => {});
  }, [handle]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 pt-24 text-center px-5">
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl">PIECE NOT FOUND</h1>
        <Link to="/shop" className="bg-bone text-ink font-mono text-xs tracking-[0.2em] px-8 py-4 hover:bg-ember hover:text-white transition-colors">BACK TO SHOP</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="pt-40 pb-24 max-w-[1500px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-8"><div className="aspect-[4/5] bg-card animate-pulse" /><div className="aspect-[4/5] bg-card animate-pulse hidden md:block" /></div>;
  }

  const colourNames = product.colors.map((c) => c.name);
  const currentColour = colourNames[colour];
  const sizesForColour = product.variants.filter((v) => v.colour === currentColour);
  const availableCount = product.variants.filter((v) => v.available).length;
  const totalCount = product.variants.length;
  const selectedVariant = size ? sizesForColour.find((v) => v.size === size) : null;
  // only reviews left by customers for THIS exact product
  const productReviews = REVIEWS.filter((r) => r.product === product.title);

  const addToCart = () => {
    if (!selectedVariant || !selectedVariant.available) return;
    add({
      variantId: selectedVariant.id,
      handle: product.handle,
      title: product.title,
      colour: selectedVariant.colour,
      size: selectedVariant.size,
      price: product.price,
      image: product.images[0],
      preorder: product.preorder,
      sellingPlanId: product.sellingPlanId || null,
    });
    toast.success(`Added — ${product.title} (${selectedVariant.colour} / ${selectedVariant.size})`);
  };

  const buyNow = () => {
    if (!selectedVariant || !selectedVariant.available) return;
    submitShopifyCheckout([
      {
        variantId: selectedVariant.id,
        qty: 1,
        preorder: product.preorder,
        sellingPlanId: product.sellingPlanId || null,
      },
    ]);
  };

  const related = all.filter((p) => p.handle !== product.handle).slice(0, 3);

  return (
    <div className="pt-28 md:pt-36 pb-28 lg:pb-24">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <nav className="font-mono text-[10px] tracking-[0.18em] uppercase text-ash mb-8 flex items-center gap-2" data-testid="pdp-breadcrumb">
          <Link to="/" className="hover:text-bone transition-colors">HOME</Link> /
          <Link to="/shop" className="hover:text-bone transition-colors">SHOP</Link> /
          <span className="text-bone">{product.title}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* gallery */}
          <div>
            <div className="relative overflow-hidden bg-card aspect-[4/5] clip-frame" data-testid="pdp-gallery-main">
              <AnimatePresence mode="wait">
                <motion.img
                  key={imgIdx}
                  src={product.images[imgIdx]}
                  alt={product.title}
                  initial={{ opacity: 0, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </AnimatePresence>
              {product.tag && <span className="absolute top-4 left-4 bg-bone text-ink font-mono text-[9px] tracking-[0.18em] px-3 py-2">{product.tag}</span>}
              {product.badge && <span className="absolute top-4 right-4 bg-ember text-white font-mono text-[9px] tracking-[0.18em] px-3 py-2">{product.badge}</span>}
            </div>
            <div className="grid grid-cols-5 gap-2 mt-2" data-testid="pdp-gallery-thumbs">
              {product.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={`aspect-square overflow-hidden bg-card border transition-colors duration-300 ${imgIdx === i ? "border-bone" : "border-transparent hover:border-white/30"}`}
                  data-testid={`pdp-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>

          {/* info */}
          <div className="lg:sticky lg:top-36 lg:self-start">
            {productReviews.length > 0 && (
              <button
                onClick={() => document.getElementById("pdp-reviews")?.scrollIntoView({ behavior: "smooth" })}
                className="flex items-center gap-2 mb-4 group"
                data-testid="pdp-review-stars"
              >
                <span className="flex text-amber-400">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </span>
                <span className="font-mono text-[10px] tracking-[0.14em] text-ash group-hover:text-bone uppercase underline underline-offset-4 transition-colors">
                  {REVIEW_RATING} — {productReviews.length} {productReviews.length === 1 ? "review" : "reviews"}
                </span>
              </button>
            )}

            <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl" data-testid="pdp-title">
              {product.title}
            </h1>
            <p className="font-display font-extrabold text-2xl mt-4" data-testid="pdp-price">{gbp(product.price)}</p>
            <p className="text-ash text-sm leading-relaxed mt-4 max-w-md">{product.subtitle}</p>

            {product.badge && (
              <div className="mt-6 flex items-center gap-2.5 bg-ember/10 border border-ember/40 text-ember px-4 py-3" data-testid="pdp-urgency-banner">
                <Flame className="w-4 h-4 shrink-0" />
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.14em] uppercase">
                  {product.preorder
                    ? `Preorder — ${product.preorderDispatch || "dispatch timeframe to be confirmed"}`
                    : product.handle === "01-signature-trucker-cap"
                    ? "Limited first drop — no restock"
                    : "Low stock — selling fast"}
                </span>
              </div>
            )}

            {/* colour */}
            <div className="mt-8">
              <p className="font-mono text-[10px] tracking-[0.22em] text-ash uppercase mb-3">
                Colour — <span className="text-bone">{currentColour}</span>
              </p>
              <div className="flex gap-2.5">
                {product.colors.map((c, i) => (
                  <button
                    key={c.name}
                    onClick={() => {
                      setColour(i);
                      setSize(null);
                    }}
                    className={`w-10 h-10 border-2 transition-all duration-300 ${colour === i ? "border-bone scale-110" : "border-white/15 hover:border-white/50"}`}
                    style={{ backgroundColor: c.hex }}
                    aria-label={`Colour ${c.name}`}
                    title={c.name}
                    data-testid={`pdp-colour-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                  />
                ))}
              </div>
            </div>

            {/* size */}
            {product.sizes.length > 1 ? (
              <div className="mt-7" ref={sizeRowRef}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-mono text-[10px] tracking-[0.22em] text-ash uppercase">Size {size && <span className="text-bone">— {size}</span>}</p>
                  <button onClick={() => setShowGuide(true)} className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.16em] text-bone underline underline-offset-4 hover:text-ember uppercase transition-colors" data-testid="pdp-size-guide-trigger">
                    <Ruler className="w-3.5 h-3.5" /> Size guide
                  </button>
                </div>
                <div className={`flex flex-wrap gap-2.5 transition-all duration-500 rounded ${sizePulse ? "ring-1 ring-ember bg-ember/5 p-2.5 -m-2.5" : ""}`} data-testid="pdp-size-row">
                  {sizesForColour.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setSize(v.size)}
                      disabled={!v.available}
                      className={`font-mono text-sm px-6 py-4 min-w-[64px] transition-all duration-300 ${
                        !v.available
                          ? "text-ash/50 line-through cursor-not-allowed border border-white/10"
                          : size === v.size
                          ? "bg-bone text-ink font-bold scale-105 shadow-[0_0_0_1px_#f2f2ef]"
                          : "border border-white/25 hover:border-bone"
                      }`}
                      data-testid={`pdp-size-picker-${v.size.toLowerCase()}`}
                    >
                      {v.size}
                    </button>
                  ))}
                </div>
                <p className="font-mono text-[9px] tracking-[0.14em] text-ash uppercase mt-3.5" data-testid="pdp-size-availability">
                  {availableCount} of {totalCount} sizes in stock
                </p>
                <div className="mt-5 border border-white/10 bg-white/[0.03] px-4 py-3.5" data-testid="pdp-fit-note">
                  <p className="font-mono text-[9px] tracking-[0.2em] text-ember uppercase mb-1.5">Fit — oversized</p>
                  <p className="text-ash text-xs leading-relaxed">Boxy oversized fit with drop shoulders — true to size for the intended look. Take one size up for a baggier fit.</p>
                </div>
              </div>
            ) : (
              <input type="hidden" value={product.variants[0].id} data-testid="pdp-size-picker-one-size" />
            )}

            {/* delivery & returns — clear before adding to bag (claims per shipping/returns policy) */}
            <div className="mt-7 border border-white/10 divide-y divide-white/10" data-testid="pdp-delivery-info">
              {[
                { icon: Truck, label: "UK standard 2\u20135 working days — free over \u00A375" },
                { icon: Clock, label: "Dispatched within 24\u201348 hours of ordering" },
                { icon: RefreshCw, label: "14-day returns on unworn items" },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-3 px-4 py-3">
                  <t.icon className="w-3.5 h-3.5 text-ember shrink-0" strokeWidth={1.5} />
                  <span className="font-mono text-[9px] md:text-[10px] tracking-[0.12em] uppercase text-bone/80">{t.label}</span>
                </div>
              ))}
            </div>

            <div ref={actionsRef} className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={addToCart}
                disabled={!selectedVariant || !selectedVariant.available}
                className="flex-1 bg-bone text-ink font-mono text-xs tracking-[0.22em] py-4.5 px-8 py-4 hover:bg-ember hover:text-white transition-colors duration-300 disabled:opacity-40 disabled:pointer-events-none"
                data-testid="pdp-add-to-cart-button"
              >
                {selectedVariant && selectedVariant.available
                  ? product.preorder ? "PREORDER — ADD TO CART" : "ADD TO CART"
                  : size ? "UNAVAILABLE — PICK ANOTHER" : "SELECT A SIZE"}
              </button>
              <button
                onClick={buyNow}
                disabled={!selectedVariant || !selectedVariant.available}
                className="flex-1 border border-white/25 font-mono text-xs tracking-[0.22em] py-4 px-8 hover:bg-bone hover:text-ink transition-colors duration-300 disabled:opacity-40 disabled:pointer-events-none"
                data-testid="pdp-buy-now-button"
              >
                <span className="flex items-center justify-center gap-2"><Lock className="w-3.5 h-3.5" /> BUY NOW</span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mt-8" data-testid="pdp-trust-rows">
              {[
                { icon: Package, label: "Dispatched within 48 hours" },
                { icon: ArrowUpLeft, label: "14-day returns" },
                { icon: MapPin, label: "Exclusively made in London" },
                { icon: ShieldCheck, label: "Secure Shopify checkout" },
              ].map((t) => (
                <div key={t.label} className="bg-ink flex items-center gap-2.5 px-4 py-4">
                  <t.icon className="w-4 h-4 text-ash shrink-0" strokeWidth={1.5} />
                  <span className="font-mono text-[9px] md:text-[10px] tracking-[0.1em] uppercase text-bone/80">{t.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 space-y-3" data-testid="pdp-details">
              {product.details.map((d) => (
                <p key={d} className="font-mono text-[10px] md:text-[11px] tracking-[0.1em] uppercase text-ash flex gap-3">
                  <span className="text-ember">+</span> {d}
                </p>
              ))}
            </div>

            <div className="mt-10 border-t border-white/10 pt-8">
              {product.description.map((p, i) => (
                <p key={i} className="text-ash text-sm leading-relaxed mb-4 max-w-lg">{p}</p>
              ))}
            </div>
          </div>
        </div>

        {/* reviews — only reviews left for THIS exact product */}
        {productReviews.length > 0 && (
          <div id="pdp-reviews" className="mt-20 md:mt-28 border-t border-white/10 pt-12" data-testid="pdp-reviews-section">
            <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
              <h2 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl">WORN. RATED. REPEATED.</h2>
              <p className="font-mono text-[10px] tracking-[0.16em] text-ash uppercase" data-testid="pdp-reviews-summary">{REVIEW_RATING} / 5 — {productReviews.length} {productReviews.length === 1 ? "review" : "reviews"} for this piece</p>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {productReviews.map((r, i) => (
                <Reveal key={r.name} delay={i * 0.06} className="bg-ink">
                  <figure className="p-7 h-full flex flex-col gap-4">
                    <span className="flex text-amber-400 gap-0.5">
                      {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-current" />)}
                    </span>
                    <blockquote className="text-sm leading-relaxed text-bone/90 flex-1" data-testid={`pdp-review-quote-${i + 1}`}>"{r.quote}"</blockquote>
                    <figcaption className="font-mono text-[10px] tracking-[0.12em] text-ash uppercase mt-auto">{r.name} — {r.location}</figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* related */}
        {related.length > 0 && (
          <div className="mt-28 md:mt-40">
            <Reveal className="flex items-end justify-between mb-10">
              <h2 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl">COMPLETE THE FIT</h2>
              <Link to="/shop" className="font-mono text-[10px] tracking-[0.2em] text-ash hover:text-bone uppercase transition-colors">VIEW ALL →</Link>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-12 md:gap-x-6">
              {related.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>

      {/* sticky mobile bar — appears only after the buy box scrolls out of view */}
      <AnimatePresence>
        {showSticky && (
          <motion.div
            initial={{ y: 90 }}
            animate={{ y: 0 }}
            exit={{ y: 90 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="lg:hidden fixed bottom-0 inset-x-0 z-[65] bg-ink/95 backdrop-blur-xl border-t border-white/10 px-5 pt-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))] flex items-center gap-3"
            data-testid="pdp-sticky-bar"
          >
            <div className="min-w-0">
              <p className="font-display font-bold uppercase text-xs truncate">{product.title}</p>
              <p className="font-mono text-xs text-ash">{gbp(product.price)}{size ? ` — ${currentColour} / ${size}` : ""}</p>
            </div>
            {selectedVariant && selectedVariant.available ? (
              <button
                onClick={addToCart}
                className="ml-auto shrink-0 bg-bone text-ink font-mono text-[11px] tracking-[0.14em] px-5 py-3.5 hover:bg-ember hover:text-white transition-colors"
                data-testid="pdp-sticky-add-button"
              >
                {product.preorder ? `PREORDER — ${gbp(product.price)}` : `ADD TO BAG — ${gbp(product.price)}`}
              </button>
            ) : (
              <button
                onClick={focusSizes}
                className="ml-auto shrink-0 bg-ember text-white font-mono text-[11px] tracking-[0.14em] px-5 py-3.5 hover:bg-bone hover:text-ink transition-colors"
                data-testid="pdp-sticky-size-button"
              >
                {product.sizes.length > 1 ? "SELECT SIZE" : "ADD TO BAG"}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>{showGuide && <SizeGuide guide={product.sizeGuide} onClose={() => setShowGuide(false)} />}</AnimatePresence>
    </div>
  );
}
